import {
  FormTemplateFieldResponse,
  FormTemplateResponse,
} from 'mapistry-shared';
import { useCallback, useEffect, useState } from 'react';

export const useEditableFormTemplateState = ({
  passedInFormTemplate,
  setIsDirty,
}: {
  passedInFormTemplate?: FormTemplateResponse;
  setIsDirty: (isDirty: boolean) => void;
}) => {
  const [formTemplate, setFormTemplate] = useState<FormTemplateResponse>();
  const [activeGroupId, setActiveGroupId] = useState<string>();

  useEffect(() => {
    setFormTemplate(passedInFormTemplate);
  }, [passedInFormTemplate]);

  useEffect(() => {
    const stillHasGroupWithActiveId = passedInFormTemplate?.groups.some(
      (group) => group.id === activeGroupId,
    );
    if (!stillHasGroupWithActiveId) {
      setActiveGroupId(passedInFormTemplate?.groups[0]?.id);
    }
  }, [passedInFormTemplate, activeGroupId]);

  const updateGroupName = useCallback(
    (updatedName: string) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        const updatedGroups = oldFormTemplate.groups.map((g) =>
          g.id === activeGroupId ? { ...g, name: updatedName } : g,
        );

        return { ...oldFormTemplate, groups: updatedGroups };
      });
      setIsDirty(true);
    },
    [activeGroupId, setIsDirty],
  );

  const updateGroupOrder = useCallback(
    (updatedGroupOrder: string[]) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        const newlyOrderedGroups = updatedGroupOrder.map(
          // we know that only valid group ids are in the group order
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          (groupId) => oldFormTemplate.groups.find((g) => g.id === groupId)!,
        );

        return { ...oldFormTemplate, groups: newlyOrderedGroups };
      });
      setIsDirty(true);
    },
    [setIsDirty],
  );

  const updateFieldOrder = useCallback(
    (updatedFieldOrder: string[]) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;

        const updatedGroups = oldFormTemplate.groups.map((group) =>
          group.id === activeGroupId
            ? {
                ...group,
                fields: updatedFieldOrder.map(
                  // we know that only valid field ids are in the field order
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  (fieldId) => group.fields.find((f) => f.id === fieldId)!,
                ),
              }
            : group,
        );

        return { ...oldFormTemplate, groups: updatedGroups };
      });

      setIsDirty(true);
    },
    [activeGroupId, setIsDirty],
  );

  const updateField = useCallback(
    (updatedField: FormTemplateFieldResponse) => {
      setFormTemplate((oldFormTemplate) => {
        if (!oldFormTemplate) return oldFormTemplate;
        return {
          ...oldFormTemplate,
          groups: oldFormTemplate.groups.map((group) =>
            group.id === activeGroupId
              ? {
                  ...group,
                  fields: group.fields.map((f) =>
                    f.id === updatedField.id ? updatedField : f,
                  ),
                }
              : group,
          ),
        };
      });
      setIsDirty(true);
    },
    [activeGroupId, setIsDirty],
  );

  return {
    activeGroupId,
    formTemplate,
    setActiveGroupId,
    setIsDirty,
    updateField,
    updateFieldOrder,
    updateGroupName,
    updateGroupOrder,
  };
};
